import { useState, type ReactNode, type FormEvent } from 'react';
import { useSignIn, useSignUp } from '@clerk/clerk-react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang, LangSwitcher } from '../lib/i18n.js';

// Fully custom auth UI built on Clerk's React SDK hooks (useSignIn / useSignUp).
// No Clerk prebuilt components — our own forms, our theme, bilingual.

function clerkErr(err: unknown, fallback: string): string {
  // Surface the real Clerk message so failures are debuggable, not generic.
  console.error('[auth] clerk error', err);
  if (err && typeof err === 'object') {
    const e = err as { errors?: Array<{ longMessage?: string; message?: string }>; message?: string };
    if (Array.isArray(e.errors) && e.errors.length) {
      return e.errors[0]?.longMessage || e.errors[0]?.message || fallback;
    }
    if (typeof e.message === 'string' && e.message) return e.message;
  }
  return fallback;
}

// ─── Shared branded shell ─────────────────────────────────────────────────
function AuthShell({ children, title }: { children: ReactNode; title: string }) {
  const { t } = useLang();
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-slate-50 text-slate-900">
      {/* Brand showcase — vertically centered so brand + hero read as one block */}
      <aside className="relative hidden lg:flex flex-col justify-center p-14 overflow-hidden border-e border-slate-200">
        <div className="bg-atmosphere absolute inset-0" aria-hidden="true" />
        <div className="relative z-10 max-w-md">
          <div className="latin flex items-center gap-3 text-4xl font-extrabold tracking-tight">
            <span className="w-3 h-3 rounded-full bg-brand-500 shadow-[0_0_20px_var(--accent-glow)]" />
            ChatRunner
          </div>
          <h2 className="mt-10 text-3xl font-extrabold leading-tight tracking-tight">
            {t('auth.hero.line1')}<br />
            <span className="text-brand-500">{t('auth.hero.line2')}</span>
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">{t('auth.hero.sub')}</p>
          <ul className="mt-8 space-y-3 text-slate-700">
            {['auth.feature.1', 'auth.feature.2', 'auth.feature.3'].map((k) => (
              <li key={k} className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-brand-500/15 text-brand-500 grid place-items-center text-xs">✓</span>
                {t(k)}
              </li>
            ))}
          </ul>
        </div>
        <div className="absolute bottom-10 inset-x-14 text-xs text-slate-500 latin">By BYTEIQ LTD · MYSOOQ.APP</div>
      </aside>

      {/* Form panel — big wordmark, title grouped tightly beneath it */}
      <main className="relative flex flex-col items-center justify-center p-6 sm:p-12">
        <div className="absolute top-5 end-5"><LangSwitcher /></div>
        <div className="w-full max-w-sm">
          <div className="latin flex items-center gap-3 text-3xl font-extrabold tracking-tight">
            <span className="w-3 h-3 rounded-full bg-brand-500 shadow-[0_0_18px_var(--accent-glow)]" />
            ChatRunner
          </div>
          <h1 className="mt-6 text-xl font-semibold tracking-tight text-slate-900">{title}</h1>
          <p className="mt-1 mb-8 text-sm text-slate-500 latin">{t('brand.tagline')}</p>
          {children}
        </div>
      </main>
    </div>
  );
}

// ─── Reusable field + button styling ──────────────────────────────────────
const inputCls =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-500 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none';
const primaryBtn =
  'w-full rounded-md bg-brand-600 text-white py-2.5 text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 shadow-[0_10px_40px_-12px_var(--accent-glow)] transition';

function GoogleButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  const { t } = useLang();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full rounded-md border border-slate-300 bg-white py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-100 disabled:opacity-50 flex items-center justify-center gap-2 transition"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      {t('auth.google')}
    </button>
  );
}

function Divider() {
  const { t } = useLang();
  return (
    <div className="flex items-center gap-3 my-5">
      <div className="h-px flex-1 bg-slate-200" />
      <span className="text-xs text-slate-500">{t('auth.or')}</span>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

// ─── Sign in (password → optional 2FA second factor) ────────────────────────
export function SignInPage() {
  const { t } = useLang();
  const { isLoaded, signIn, setActive } = useSignIn();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'password' | 'twofa'>('password');
  // The ACTUAL second-factor strategy read from supportedSecondFactors — we
  // don't assume it (email_code, phone_code, totp, backup_code, ...).
  const [twoFa, setTwoFa] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function finish(sessionId: string | null | undefined) {
    if (!setActive) return;
    await setActive({ session: sessionId });
    navigate('/');
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isLoaded) return;
    setBusy(true); setError(null);
    try {
      const res = await signIn.create({ identifier: email });
      const afterPw = res.status === 'complete'
        ? res
        : await signIn.attemptFirstFactor({ strategy: 'password', password });

      if (afterPw.status === 'complete') {
        await finish(afterPw.createdSessionId);
        return;
      }
      if (afterPw.status === 'needs_second_factor') {
        // Read the ACTUAL configured second factor — no assumptions.
        const factors = afterPw.supportedSecondFactors ?? [];
        console.log('[auth] supportedSecondFactors', factors);
        const strategy = factors[0]?.strategy;
        if (!strategy) {
          setError(`${t('auth.err.generic')} [no_2fa_factors]`);
          return;
        }
        // Code-delivery factors (email/SMS) must be prepared so Clerk sends the
        // code. TOTP/backup are read by the user — nothing to send.
        if (strategy === 'email_code' || strategy === 'phone_code') {
          await signIn.prepareSecondFactor(
            { strategy } as Parameters<typeof signIn.prepareSecondFactor>[0],
          );
        }
        setTwoFa(strategy);
        setStage('twofa');
        setCode('');
        return;
      }
      setError(`${t('auth.err.generic')} [${afterPw.status}]`);
    } catch (err) {
      setError(clerkErr(err, t('auth.err.generic')));
    } finally {
      setBusy(false);
    }
  }

  async function onTwoFa(e: FormEvent) {
    e.preventDefault();
    if (!isLoaded) return;
    setBusy(true); setError(null);
    try {
      const attempt = await signIn.attemptSecondFactor(
        { strategy: twoFa, code } as Parameters<typeof signIn.attemptSecondFactor>[0],
      );
      if (attempt.status === 'complete') {
        await finish(attempt.createdSessionId);
        return;
      }
      setError(`${t('auth.err.generic')} [${attempt.status}]`);
    } catch (err) {
      setError(clerkErr(err, t('auth.err.generic')));
    } finally {
      setBusy(false);
    }
  }

  async function google() {
    if (!isLoaded) return;
    try {
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: '/sso-callback',
        redirectUrlComplete: '/',
      });
    } catch (err) {
      setError(clerkErr(err, t('auth.err.generic')));
    }
  }

  // ── 2FA stage ──
  if (stage === 'twofa') {
    return (
      <AuthShell title={t('auth.2fa.title')}>
        <p className="text-sm text-slate-500 mb-5">
          {twoFa === 'email_code'
            ? t('auth.2fa.email.sub')
            : twoFa === 'phone_code'
            ? t('auth.2fa.phone.sub')
            : twoFa === 'totp'
            ? t('auth.2fa.totp.sub')
            : t('auth.2fa.backup.sub')}
        </p>
        <form onSubmit={onTwoFa} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.2fa.code')}</label>
            <input
              inputMode="numeric"
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className={inputCls + ' tracking-[0.4em] text-center text-lg'}
            />
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <button type="submit" disabled={busy || !isLoaded} className={primaryBtn}>
            {busy ? t('auth.working') : t('auth.2fa.btn')}
          </button>
        </form>
        <button
          onClick={() => { setStage('password'); setError(null); }}
          className="mt-4 text-sm text-slate-500 hover:text-slate-900 w-full text-center"
        >
          {t('auth.2fa.back')}
        </button>
      </AuthShell>
    );
  }

  // ── Password stage ──
  return (
    <AuthShell title={t('auth.signin.title')}>
      <GoogleButton onClick={google} disabled={!isLoaded} />
      <Divider />
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.email')}</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('auth.email.ph')} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.password')}</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.password.ph')} className={inputCls} />
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        <button type="submit" disabled={busy || !isLoaded} className={primaryBtn}>
          {busy ? t('auth.working') : t('auth.signinBtn')}
        </button>
      </form>
      <p className="mt-6 text-sm text-slate-500 text-center">
        {t('auth.noAccount')}{' '}
        <Link to="/sign-up" className="text-brand-500 hover:underline font-medium">{t('auth.toSignup')}</Link>
      </p>
    </AuthShell>
  );
}

// ─── Sign up (email/password + email-code verification) ─────────────────────
export function SignUpPage() {
  const { t } = useLang();
  const { isLoaded, signUp, setActive } = useSignUp();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'form' | 'verify'>('form');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isLoaded) return;
    setBusy(true); setError(null);
    try {
      await signUp.create({ emailAddress: email, password });
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setStage('verify');
    } catch (err) {
      setError(clerkErr(err, t('auth.err.generic')));
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    if (!isLoaded) return;
    setBusy(true); setError(null);
    try {
      const res = await signUp.attemptEmailAddressVerification({ code });
      if (res.status === 'complete') {
        await setActive({ session: res.createdSessionId });
        navigate('/onboarding');
      } else {
        setError(t('auth.err.generic'));
      }
    } catch (err) {
      setError(clerkErr(err, t('auth.err.generic')));
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (!isLoaded) return;
    try { await signUp.prepareEmailAddressVerification({ strategy: 'email_code' }); }
    catch (err) { setError(clerkErr(err, t('auth.err.generic'))); }
  }

  async function google() {
    if (!isLoaded) return;
    try {
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: '/sso-callback',
        redirectUrlComplete: '/onboarding',
      });
    } catch (err) {
      setError(clerkErr(err, t('auth.err.generic')));
    }
  }

  if (stage === 'verify') {
    return (
      <AuthShell title={t('auth.verify.title')}>
        <p className="text-sm text-slate-500 mb-5">{t('auth.verify.sub')}</p>
        <form onSubmit={onVerify} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.verify.code')}</label>
            <input
              inputMode="numeric"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className={inputCls + ' tracking-[0.4em] text-center text-lg'}
            />
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <button type="submit" disabled={busy || !isLoaded} className={primaryBtn}>
            {busy ? t('auth.working') : t('auth.verify.btn')}
          </button>
        </form>
        <button onClick={resend} className="mt-4 text-sm text-brand-500 hover:underline w-full text-center">
          {t('auth.verify.resend')}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('auth.signup.title')}>
      <GoogleButton onClick={google} disabled={!isLoaded} />
      <Divider />
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.email')}</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('auth.email.ph')} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.password')}</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.password.ph')} className={inputCls} />
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        {/* Clerk bot-protection needs this element present on the sign-up page. */}
        <div id="clerk-captcha" />
        <button type="submit" disabled={busy || !isLoaded} className={primaryBtn}>
          {busy ? t('auth.working') : t('auth.signupBtn')}
        </button>
      </form>
      <p className="mt-6 text-sm text-slate-500 text-center">
        {t('auth.haveAccount')}{' '}
        <Link to="/sign-in" className="text-brand-500 hover:underline font-medium">{t('auth.toSignin')}</Link>
      </p>
    </AuthShell>
  );
}
